import {
  Globe,
  Inbox,
  Link,
  Headphones,
  FileText,
  Lightbulb,
  Folder,
  CheckSquare,
  Star,
  type LucideIcon,
} from 'lucide-react';
import type { Category, ItemType } from '@/types';

export interface CategoryConfig {
  id: Category;
  label: string;
  icon: LucideIcon;
  description?: string;
}

export const CATEGORIES: readonly CategoryConfig[] = [
  { id: 'inbox', label: 'Inbox', icon: Inbox, description: 'Items to process' },
  { id: 'all', label: 'All', icon: Globe, description: 'View all items' },
  { id: 'link', label: 'Links', icon: Link, description: 'Saved links and articles' },
  { id: 'podcast', label: 'Podcasts', icon: Headphones, description: 'Podcast episodes' },
  { id: 'note', label: 'Notes', icon: FileText, description: 'Personal notes' },
  { id: 'idea', label: 'Ideas', icon: Lightbulb, description: 'Ideas and thoughts' },
  { id: 'file', label: 'Files', icon: Folder, description: 'Uploaded files' },
  { id: 'task', label: 'Tasks', icon: CheckSquare, description: 'Tasks and to-dos' },
  { id: 'favorites', label: 'Favorites', icon: Star, description: 'Favorite items' },
] as const;

export const ITEM_TYPE_ICONS: Record<ItemType, LucideIcon> = {
  link: Link,
  podcast: Headphones,
  note: FileText,
  idea: Lightbulb,
  file: Folder,
  task: CheckSquare,
};

export const ITEM_TYPE_LABELS: Record<ItemType, string> = {
  link: 'Link',
  podcast: 'Podcast',
  note: 'Note',
  idea: 'Idea',
  file: 'File',
  task: 'Task',
};

export const ITEM_TYPES: readonly ItemType[] = [
  'link',
  'podcast',
  'note',
  'idea',
  'file',
  'task',
] as const;

/**
 * Get category config by ID
 */
export function getCategoryById(id: Category): CategoryConfig | undefined {
  return CATEGORIES.find((cat) => cat.id === id);
}

/**
 * Check if a category ID is valid
 */
export function isValidCategory(id: string): id is Category {
  return CATEGORIES.some((cat) => cat.id === id);
}

/**
 * Check if a category ID is an item type
 */
export function isItemTypeCategory(id: Category): id is ItemType {
  return ITEM_TYPES.includes(id as ItemType);
}
